import {InfoCircleOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import {Carousel, Descriptions, Image, Row, Statistic, Tooltip, Typography} from "antd";
import {Col} from "antd/es";

import {EntityLinksDropdown} from "@/components/custom/EntityLinks/EntityLinks.jsx";
import {projectCurrency, showContextLinks} from "@/properties.js";
import {Header} from "@/variants/index.js";
import {Link} from "react-router-dom";
import styles from "./HeaderInfo.module.css";
import {beautifyKey} from '@/helpers.js';

const FULL_ROW_FIELDS = ["description", "short_description"];

const isFullRow = field => FULL_ROW_FIELDS.includes(field);
const hasOddHalfRowFields = headerColumns => headerColumns.filter(f => !isFullRow(f)).length % 2 !== 0;

export const HeaderInfo = ({item, handleModalOpen, showEdit, headerColumns, images, usage}) => {
	function getLabel(field) {
		const mapping = {
			price: `Price per Unit (${projectCurrency})`,
			cl_name: item.type === "material" ? "Material Class" : "Asset Category",
			material: item.type === "material" ? "Material" : "Equipment",
			usage: "Forecast Demand",
			mat_type: "Material Type",
			mat_group: "Material Group",
			contract_lead_time: "Contract Delivery Time",
			return_ratio: "Returns Ratio",
			...Header.getLabels(item.type),
		};
		return mapping[field] || beautifyKey(field);
	}

	function getImages() {
		const arr = [...images].sort();
		arr.sort(function (a, b) {
			let keyA = a.active,
				keyB = b.active;
			if (keyA > keyB) return -1;
			if (keyA < keyB) return 1;
			return 0;
		});
		return arr;
	}

	const editing = (
		<Typography.Link
			onClick={handleModalOpen}
			style={{display: "inline-flex", alignItems: "center"}}
			data-testid="header-info-edit-link">
			{images && images.length > 0 ? <img alt="edit-image" src="/images/edit-user-blue.png" /> : "Add image"}
		</Typography.Link>
	);

	function getKeyValue(key) {
		if (key === "part_number") {
			return (
				<Link
					to={`https://www.google.com/search?q=${item[key]}`}
					target="_blank"
					rel="noopener noreferrer"
					data-testid="header-info-part-number-link">
					{item[key]}
				</Link>
			);
		} else if (key === "usage") {
			return Math.round((usage / 4) * 100) / 100;
		} else if (key === "price") {
			return <Statistic valueStyle={{fontSize: 14}} value={parseInt(item[key] * 100 || 0) / 100} />;
		} else if (key === "return_ratio") {
			return item[key] != null ? `${item[key]}%` : "0% (Not enough data)";
		}
        const value = item[key];
		return Header.formatHeaderFieldValue(key, item, value);
	}

	const hasImages = images && images.length > 0;
	const isActivelyReviewed = item.is_reviewed === true || item.is_reviewed === 1 || item.is_reviewed === "1";
	const showReviewInfo = item.type === "material" && item.review_date;
	const reviewStyles = {
		label: {fontWeight: "bold", backgroundColor: "#e6f4ff"},
		content: {backgroundColor: "#e6f4ff"},
	};

	const imagePanelStyle = {
		position: "absolute",
		top: 0,
		right: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-end",
		justifyContent: hasImages ? "center" : "flex-start",
		padding: "4px 8px 4px 8px",
		minHeight: "100%",
	};

	return (
		<div data-testid="header-info-container">
			<h3 className={styles.title}>
				Item:{" "}
				<b>
					{item.operator} {item.material}
				</b>
				{showContextLinks && (
					<EntityLinksDropdown
						operator={item.operator}
						sortCode={item.sort_field}
						material={item.material}
						type={item.type}
					/>
				)}
			</h3>
			<div className={styles.descriptionWrapper}>
				<Row>
					<Col span={15}>
						{showReviewInfo && (
							<Descriptions
								bordered
								size="small"
								column={isActivelyReviewed && item.review_expiry_date ? 2 : 1}
								style={{marginBottom: 10}}
								styles={reviewStyles}>
								<Descriptions.Item
									label={
										<span>
											Last DIGATEX Review Date{" "}
											<Tooltip
												title={
													<div>
														<div>Reviewed by: {item.reviewed_by || "NA"}</div>
														{item.review_notes && <div>Notes: {item.review_notes}</div>}
													</div>
												}>
												<InfoCircleOutlined />
											</Tooltip>
										</span>
									}>
									{item.review_date?.split("T")[0]}
								</Descriptions.Item>
								{isActivelyReviewed && item.review_expiry_date && (
									<Descriptions.Item label="Review Expiry Date">
										{item.review_expiry_date?.split("T")[0]}
									</Descriptions.Item>
								)}
							</Descriptions>
						)}
						<Descriptions size="small" bordered column={2}>
							{headerColumns.map(field => (
								<Descriptions.Item
									key={field}
									span={isFullRow(field) ? 2 : 1}
									styles={{
										label: {width: isFullRow(field) ? "10%" : "20%"},
										content: {textAlign: "left"},
									}}
									label={<b>{getLabel(field)}</b>}>
									{getKeyValue(field)}
								</Descriptions.Item>
							))}
							{hasOddHalfRowFields(headerColumns) && (
								<Descriptions.Item key="__filler__" span={1} label={null} />
							)}
						</Descriptions>
					</Col>
				</Row>
				<div style={imagePanelStyle}>
					{hasImages ? (
						<>
							<Carousel
								arrows={true}
								style={{width: 200, textAlign: "right"}}
								prevArrow={<LeftOutlined />}
								nextArrow={<RightOutlined />}>
								{getImages().map(v => (
									<div key={v.image} style={{textAlign: "right"}}>
										<Image
											preview={false}
											src={v.image}
											height={150}
											style={{width: "auto", maxWidth: "100%"}}
										/>
									</div>
								))}
							</Carousel>
							<div className={styles.editWrapper}>{showEdit && editing}</div>
						</>
					) : (
						showEdit && editing
					)}
				</div>
			</div>
		</div>
	);
};
